import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { filter, skip } from 'rxjs/operators';
import { DialogAnimationService } from 'src/app/angular-core/services/dialog-animation.service';
import { LocalizedDatePipe } from 'src/app/angular-core/utils/pipes/localized-date/localized-date.pipe';
import { AnnotatedOrder, DealerRemoteMaintenanceOrder } from 'src/app/generated/CrossPoint.License.Server';
import { getOrdersAction, getOrderAction } from 'src/app/store/actions/licenses.actions';
import { selectLicenseOrders, selectOrder } from 'src/app/store/selectors/licenses.selectors';
import { hasValue } from 'src/app/angular-core/utils/functions/common.functions';
import { IRemoteXPACPortalState } from 'src/app/utils/interfaces/store.interfaces';
import { OrderDetailsComponent, OrderDetailsDialogData } from '../order-details/order-details.component';

@UntilDestroy()
@Component({
  selector: 'xpac-order-history',
  templateUrl: './order-history.component.html',
  styleUrls: ['./order-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderHistoryComponent implements OnInit {

  private static readonly PAGE_SIZE: number = 25;
  private static readonly SEARCH_DEBOUNCE_TIME_MS: number = 500;
  private dialogRef: any = null;

  public isLoading: boolean = true;
  public orderedLicenses: DealerRemoteMaintenanceOrder[] = [];
  public searchText: string = '';
  public searchTimeoutHandle: number | null = null;
  public isLoadingOrderDetails: boolean = false;
  public isLoadingMore: boolean = false;
  public hasMore: boolean = true;

  private loadBeforeUtc: string = null;

  constructor(private store: Store<IRemoteXPACPortalState>, private cdr: ChangeDetectorRef, private datePipe: LocalizedDatePipe, private dialog: DialogAnimationService,) { }

  public ngOnInit(): void {
    this.initListeners();
    this.initData();
  }

  private initListeners(): void {
    this.store.select(selectLicenseOrders)
      .pipe(
        // Ignore whatever was in the store to prevent issues with pagination
        skip(1),
        filter<DealerRemoteMaintenanceOrder[]>(Boolean),
        untilDestroyed(this)
      )
      .subscribe((orders: DealerRemoteMaintenanceOrder[]) => {
        // Create a copy so we can sort it
        orders = [...orders];
        // Ensure DealerRemoteMaintenanceOrder are sorted with the most recent one on top
        orders.sort((a: DealerRemoteMaintenanceOrder, b: DealerRemoteMaintenanceOrder) => -(a.createdUtc.localeCompare(b.createdUtc)));
        this.hasMore = orders.length > OrderHistoryComponent.PAGE_SIZE;
        if (this.loadBeforeUtc !== null) {
          // This is a next page, add to the list
          this.orderedLicenses.push(...orders.slice(0, OrderHistoryComponent.PAGE_SIZE));
        }
        else {
          // 'first time' load, replace the list
          this.orderedLicenses = orders.slice(0, OrderHistoryComponent.PAGE_SIZE);
        }
        this.loadBeforeUtc = this.orderedLicenses[this.orderedLicenses.length - 1]?.createdUtc ?? null;
        this.isLoading = false;
        this.isLoadingMore = false;
        this.cdr.markForCheck();
      });
  }

  private initData(): void {
    this.store.dispatch(getOrdersAction({
      data: {
        // Request 1 more so we know if there is more data after this page
        maxItems: OrderHistoryComponent.PAGE_SIZE + 1,
      }
    }));
  }

  public getInfoString(order: DealerRemoteMaintenanceOrder): string {
    let info: string = "";

    if (order.createdUtc != "") {
      info += this.datePipe.transform(order.createdUtc, 'shortDate');
    }

    return info;
  }

  public onSearchTextChanged(searchText: string): void {
    this.searchText = searchText;

    if (this.searchTimeoutHandle !== null) {
      clearTimeout(this.searchTimeoutHandle);
    }

    // Wait some time before requesting
    this.searchTimeoutHandle = window.setTimeout(() => {
      if (!this.isLoadingMore) {
        // Reset the paging and start loading
        this.loadBeforeUtc = null;
        this.orderedLicenses = [];
        this.loadMore();
      }
    }, OrderHistoryComponent.SEARCH_DEBOUNCE_TIME_MS);
  }

  public onOrderClick(order: DealerRemoteMaintenanceOrder): void {

    if(!this.isLoadingOrderDetails){
      this.isLoadingOrderDetails = true;
      this.cdr.markForCheck();
  
      // first get the order details
      this.store.dispatch(getOrderAction({ data: order.id }));
      
      this.store.select(selectOrder(order.id))
        .pipe(
          filter(hasValue),
          untilDestroyed(this)
        )
        .subscribe((orderLicenses: AnnotatedOrder) => {
          if (orderLicenses.id === order.id) {
            const orderDetailsDialogData: OrderDetailsDialogData = { dealerRemoteMaintenanceOrder: order, annotatedOrder: orderLicenses };
            if (this.dialogRef === null) {
              this.dialogRef = this.dialog.open(OrderDetailsComponent, {
                panelClass: 'my-full-screen-dialog',
                animation: { to: "left" },
                data: orderDetailsDialogData,
                disableClose: true
              });

              this.dialogRef.afterClosed().subscribe((result: boolean) => {
                this.isLoadingOrderDetails = false;
                this.dialogRef = null;
                this.cdr.markForCheck();
              });
            }
          }
        }); 
      }
  }

  public loadMore(): void {
    this.isLoadingMore = true;
    this.store.dispatch(getOrdersAction({
      data: {
        endUtc: this.loadBeforeUtc,
        searchText: this.searchText,
        maxItems: OrderHistoryComponent.PAGE_SIZE + 1,
      }
    }));
    this.cdr.markForCheck();
  }
}